import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RotateCcw, Sparkles, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_STYLE = {
  student: { gradient: "from-purple-500 to-pink-500" },
  parent: { gradient: "from-blue-500 to-indigo-500" },
  professional: { gradient: "from-emerald-500 to-teal-500" },
};

export default function PatriciaPageGuide({ pageId }) {
  const { getAuthHeader, user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [knowledge, setKnowledge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSections, setShowSections] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const typeRef = useRef(null);
  const audioRef = useRef(null);
  const role = user?.role === 'professional' ? 'professional' : user?.role === 'parent' ? 'parent' : 'student';
  const style = ROLE_STYLE[role] || ROLE_STYLE.student;

  useEffect(() => {
    return () => {
      if (typeRef.current) clearInterval(typeRef.current);
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  const playAudio = useCallback(() => {
    if (!voiceEnabled) return;
    const headers = getAuthHeader()?.headers || {};
    fetch(`${API}/knowledge/tts/${pageId}`, { headers })
      .then(res => { if (res.ok) return res.blob(); throw new Error('TTS failed'); })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        if (audioRef.current) { audioRef.current.pause(); URL.revokeObjectURL(audioRef.current.src); }
        audioRef.current = new Audio(url);
        audioRef.current.onplay = () => setIsSpeaking(true);
        audioRef.current.onended = () => setIsSpeaking(false);
        audioRef.current.onpause = () => setIsSpeaking(false);
        audioRef.current.onerror = () => setIsSpeaking(false);
        audioRef.current.play().catch(() => setIsSpeaking(false));
      })
      .catch(() => {
        // No robotic fallback, a real voice or none at all.
        setIsSpeaking(false);
      });
  }, [voiceEnabled, pageId, getAuthHeader]);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setIsSpeaking(false);
  };

  const fetchAndStart = async () => {
    if (knowledge) { startTyping(knowledge.patricia_script); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/knowledge/page/${pageId}`, getAuthHeader());
      setKnowledge(res.data);
      startTyping(res.data.patricia_script);
    } catch {
      const fb = "I can help you navigate this page.";
      setKnowledge({ patricia_script: fb, title: pageId, sections: [], what_you_can_do: [], next_step: "" });
      startTyping(fb);
    } finally { setLoading(false); }
  };

  const startTyping = (text) => {
    if (typeRef.current) clearInterval(typeRef.current);
    setTypedText(''); setIsTyping(true); setShowSections(false);
    let idx = 0;
    typeRef.current = setInterval(() => {
      idx++;
      setTypedText(text.slice(0, idx));
      if (idx >= text.length) { clearInterval(typeRef.current); setIsTyping(false); setTimeout(() => setShowSections(true), 300); }
    }, 18);
  };

  const handleOpen = () => { setOpen(true); setShowSections(false); fetchAndStart(); };
  const handleReplay = () => { setShowSections(false); stopAudio(); if (knowledge) { startTyping(knowledge.patricia_script); setTimeout(() => playAudio(), 400); } };
  const handleClose = () => { setOpen(false); stopAudio(); if (typeRef.current) clearInterval(typeRef.current); };
  const toggleVoice = () => { if (isSpeaking) stopAudio(); setVoiceEnabled(v => !v); };

  if (!isAuthenticated) return null;

  return (
    <>
      {!open && (
        <motion.button initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          onClick={handleOpen}
          className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-gradient-to-r ${style.gradient} text-white text-xs font-medium shadow-lg hover:shadow-xl hover:scale-105 transition-all`}
          data-testid="ask-patricia-btn">
          <Sparkles className="w-3.5 h-3.5" />
          Ask Patricia
        </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 5, scale: 0.97 }}
            transition={{ type: "spring", damping: 25, stiffness: 350 }}
            className="mt-3 rounded-2xl bg-slate-900/95 border border-slate-700/50 backdrop-blur-xl overflow-hidden shadow-2xl"
            data-testid="patricia-guide-panel">
            <div className={`h-0.5 bg-gradient-to-r ${style.gradient}`} />
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <motion.div
                    animate={isSpeaking ? { scale: [1, 1.15, 1] } : {}}
                    transition={{ repeat: Infinity, duration: 0.8 }}
                    className={`w-7 h-7 rounded-full bg-gradient-to-br ${style.gradient} flex items-center justify-center shrink-0`}>
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </motion.div>
                  <div>
                    <span className="text-white font-bold text-xs">Patricia</span>
                    <span className="text-slate-300 text-[10px] ml-2">
                      {isSpeaking ? 'Speaking...' : isTyping ? 'Typing...' : knowledge?.title || ''}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-0.5">
                  <button onClick={toggleVoice} className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors" data-testid="patricia-voice-toggle">
                    {voiceEnabled ? <Volume2 className="w-3 h-3 text-blue-400" /> : <VolumeX className="w-3 h-3 text-slate-300" />}
                  </button>
                  <button onClick={handleReplay} className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors" data-testid="patricia-replay">
                    <RotateCcw className="w-3 h-3 text-slate-300" />
                  </button>
                  <button onClick={handleClose} className="p-1.5 hover:bg-slate-800 rounded-lg transition-colors" data-testid="patricia-close">
                    <X className="w-3 h-3 text-slate-300" />
                  </button>
                </div>
              </div>

              {/* Script */}
              <div className="mb-3 p-3 bg-slate-800/50 rounded-xl border border-slate-700/30 min-h-[3rem]">
                {loading ? (
                  <div className="flex items-center gap-2">
                    {[0,1,2].map(i => (
                      <motion.div key={i} animate={{ opacity: [0.3,1,0.3] }} transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15 }}
                        className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${style.gradient}`} />
                    ))}
                    <span className="text-slate-300 text-xs">Preparing...</span>
                  </div>
                ) : (
                  <p className="text-slate-200 text-xs leading-relaxed">
                    {typedText}{isTyping && <span className="animate-pulse text-purple-400">|</span>}
                  </p>
                )}
              </div>

              {/* Page Sections */}
              <AnimatePresence>
                {showSections && knowledge?.sections?.length > 0 && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mb-3 overflow-hidden">
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wider mb-1.5">What's on this page</p>
                    <div className="grid grid-cols-2 gap-1">
                      {knowledge.sections.slice(0, 6).map((s, i) => (
                        <motion.div key={i} initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                          className="p-1.5 bg-slate-800/40 rounded-lg border border-slate-700/20">
                          <p className="text-white text-[10px] font-medium">{s.name}</p>
                          <p className="text-slate-300 text-[9px] line-clamp-1">{s.desc}</p>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {showSections && knowledge?.next_step && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="mb-3 p-2 bg-blue-500/8 rounded-lg border border-blue-500/15">
                  <p className="text-blue-400 text-[10px]"><span className="font-bold">Next step:</span> {knowledge.next_step}</p>
                </motion.div>
              )}

              <Button size="sm" variant="ghost" onClick={handleClose} className="w-full text-xs text-slate-300 hover:bg-slate-800 h-7">
                Got It
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
